// Kapabilitetsplanlegger: behovsbasert kapabilitetsanbefaling, plassering av
// sensorer/droner/observasjonsposter i kartet, og bestilling (be om / godkjenn
// / avvis / utplasser en kapabilitet).
//
// ── HVORFOR DENNE FILA FINNES ────────────────────────────────────────────────
// EIERMÅL 2026-08-19 (vale/RFI_SENSORPLASSERING_LLM_PLAN_2026-08-19.md §3/§8):
// «lag system for bestilling og plassering, i GNÅ og SKADE». Kapabilitet-
// registeret og anbefaleren (@skade/contracts/kapabilitet.ts) og hub-flaten
// (services/hub/src/kapabilitet.ts) fantes — INGEN operatør kunne nå dem. Samme
// klasse feil som personregisteret løste natten før (Bølge 233): en HTTP-flate
// ingen kan trykke på, er ikke en flate.
//
// Docked panel, hooks-injisert nettverk (testbart uten fetch — samme mønster
// som ui/personregister.ts), ren DOM. Rekkevidde-ringene tegnes av CopMap
// (`tegnKapabiliteter`) — geometrien bygges rent HER (`kapabiliteterFeatureCollection`),
// testbar uten MapLibre.
import {
  KapabilitetSchema, KapabilitetType, KAPABILITET_TYPE_LABEL,
  BESTILLING_STATUS_LABEL, nyKapabilitetMedProfil, ringPolygon, featureCollection,
  type Kapabilitet, type Bestilling, type GeoFeature, type GeoFeatureCollection,
  type KapabilitetForslag,
} from '@skade/contracts';
import { sporTekst } from './shell.js';

// ── Rene funksjoner (testbare uten DOM/MapLibre) ────────────────────────────

export interface KapabiliteterSvar {
  kapabiliteter: Kapabilitet[];
  formfeil?: string;
}

/** Les `GET /kapabilitet`-svaret. Samme disiplin som `lesBrukereSvar` i
 * personregister.ts: en feilet henting er ALDRI et tomt register. */
export function lesKapabiliteterSvar(u: unknown): KapabiliteterSvar {
  const raa = (u as Record<string, unknown> | null)?.['kapabiliteter'];
  if (!Array.isArray(raa)) {
    return { kapabiliteter: [], formfeil: 'Svaret fra hubben hadde ikke et «kapabiliteter»-felt.' };
  }
  const kapabiliteter: Kapabilitet[] = [];
  let forkastet = 0;
  for (const x of raa) {
    const p = KapabilitetSchema.safeParse(x);
    if (p.success) kapabiliteter.push(p.data); else forkastet += 1;
  }
  return {
    kapabiliteter,
    ...(forkastet > 0 ? { formfeil: `${forkastet} post(er) i svaret var ugyldige og er utelatt.` } : {}),
  };
}

export interface BestillingerSvar {
  bestillinger: Bestilling[];
  formfeil?: string;
}

export function lesBestillingerSvar(u: unknown): BestillingerSvar {
  const raa = (u as Record<string, unknown> | null)?.['bestillinger'];
  if (!Array.isArray(raa)) {
    return { bestillinger: [], formfeil: 'Svaret fra hubben hadde ikke et «bestillinger»-felt.' };
  }
  // Bestilling har ingen egen zod-safeParse-eksport i denne fila (kontrakten
  // eksporterer `BestillingSchema`, men vi trenger ikke duplisere valideringen
  // her — feltnavnene brukt under (`id`, `status`, `behovTekst`, …) leses
  // defensivt uansett).
  return { bestillinger: raa as Bestilling[] };
}

/**
 * Bygg rekkevidde-ring(er) + etikett-punkt for hver kapabilitet, til
 * `CopMap.tegnKapabiliteter`. Ren funksjon — testbar uten MapLibre.
 *
 * `kyp` (og enhver annen type med `identifiseringsrekkeviddeM`) får TO ringer:
 * den ytre (deteksjon) og en indre, stiplet (identifisering) — «detect-then-
 * classify»-mønsteret fra planen §3/§4.
 */
export function kapabiliteterFeatureCollection(liste: readonly Kapabilitet[]): GeoFeatureCollection {
  const features: GeoFeature[] = [];
  for (const k of liste) {
    features.push({
      type: 'Feature',
      geometry: { type: 'Polygon', coordinates: [ringPolygon({ lat: k.lat, lon: k.lon }, k.rekkeviddeM)] },
      properties: { id: k.id, identifisering: false },
    });
    if (k.identifiseringsrekkeviddeM !== undefined) {
      features.push({
        type: 'Feature',
        geometry: { type: 'Polygon', coordinates: [ringPolygon({ lat: k.lat, lon: k.lon }, k.identifiseringsrekkeviddeM)] },
        properties: { id: k.id, identifisering: true },
      });
    }
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [k.lon, k.lat] },
      properties: { id: k.id, label: k.navn?.trim() || KAPABILITET_TYPE_LABEL[k.type] },
    });
  }
  return featureCollection(features);
}

/** Bestillinger som venter på en avgjørelse (`bestilt`), nyeste først. */
export function ventendeBestillinger(liste: readonly Bestilling[]): Bestilling[] {
  return liste.filter((b) => b.status === 'bestilt').sort((a, b) => b.createdMs - a.createdMs);
}

/**
 * Godkjente bestillinger som venter på UTPLASSERING (SENS-42) — operatøren
 * har sagt ja, men ingen fysisk kapabilitet er koblet på ennå. Egen liste fra
 * `ventendeBestillinger` fordi handlingen er en annen (velg kapabilitet, ikke
 * godkjenn/avvis) og statusen er en annen (`godkjent`, ikke `bestilt`).
 */
export function klareTilUtplassering(liste: readonly Bestilling[]): Bestilling[] {
  return liste.filter((b) => b.status === 'godkjent').sort((a, b) => b.createdMs - a.createdMs);
}

// ── Panel ────────────────────────────────────────────────────────────────────

export interface KapabilitetsplanleggerHooks {
  /** Autentisert GET. Injisert slik at panelet er testbart uten nettverk. */
  hent: (sti: string) => Promise<unknown>;
  /** Autentisert POST med JSON-kropp. */
  send: (sti: string, kropp: unknown) => Promise<unknown>;
  naa: () => number;
  /** Tegn rekkevidde-ringene på kartet (CopMap.tegnKapabiliteter). */
  tegnRinger: (fc: GeoFeatureCollection) => void;
  /** Kartets nåværende senter — «bruk kartsenter» ved manuell plassering. */
  kartsenter?: () => { lat: number; lon: number };
  /**
   * KART FØRST: arm neste kartklikk for å plassere en kapabilitet av `type`
   * DER operatøren klikker (main.ts eier selve `map.on('click', …)` via
   * `kartmodus` — samme arbiter som «plasser stridsmelding»/«plasser ildmål»).
   * Panelet kaller denne og venter; main.ts kaller `plasserPaaPunkt` tilbake
   * når klikket lander. Udefinert i tester (ingen ekte kart) — knappen vises
   * da ikke.
   */
  armPlassering?: (type: Kapabilitet['type']) => void;
  /** Fly til et punkt (velg-handling i objektlisten). */
  flyTo?: (lat: number, lon: number) => void;
  /** Kalt etter hver `last()` (suksess ELLER feil) — objektregisteret (main.ts)
   * bruker denne til å melde at Objekter-lista må tegnes på nytt. */
  onEndret?: () => void;
}

const ALLE_TYPER = KapabilitetType.options;

export class KapabilitetsplanleggerPanel {
  readonly el: HTMLElement;
  private open = false;
  private meldingEl!: HTMLElement;
  private listeEl!: HTMLElement;
  private anbefalEl!: HTMLElement;
  private bestillingEl!: HTMLElement;
  private utplasserEl!: HTMLElement;
  private nyTypeSelect!: HTMLSelectElement;
  private nyLatInput!: HTMLInputElement;
  private nyLonInput!: HTMLInputElement;
  private behovInput!: HTMLTextAreaElement;

  private siste: Kapabilitet[] = [];
  private sisteBestillinger: Bestilling[] = [];
  private ventendeSletting = new Set<string>();
  private ventendeBehandling = new Set<string>();
  private feilPerId = new Map<string, string>();

  constructor(private hooks: KapabilitetsplanleggerHooks) {
    this.el = this.build();
  }

  toggle(): void {
    this.open = !this.open;
    this.el.classList.toggle('open', this.open);
    if (this.open) { void this.last(); void this.lastBestillinger(); }
  }

  private build(): HTMLElement {
    const p = div('report-composer kapabilitetsplanlegger-panel');
    const head = div('report-head');
    head.append(span('report-title', 'Kapabiliteter'));
    const x = document.createElement('button');
    x.className = 'report-x'; x.textContent = '×'; x.onclick = () => this.toggle();
    head.append(x);
    p.append(head);
    p.append(span('checklist-note',
      'Behovsbasert anbefaling, plassering og bestilling av sensorer/droner/observasjonsposter.'));

    // ── Behov → anbefaling ────────────────────────────────────────────────
    p.append(span('checklist-item-title', 'Beskriv behovet'));
    this.behovInput = document.createElement('textarea');
    this.behovInput.className = 'report-input';
    this.behovInput.placeholder = 'F.eks. «vi mangler oversikt over kystlinjen ved Håøya»';
    p.append(this.behovInput);
    const anbefalBtn = document.createElement('button');
    anbefalBtn.type = 'button'; anbefalBtn.className = 'targetlist-btn'; anbefalBtn.textContent = 'Anbefal kapabilitet';
    anbefalBtn.onclick = () => void this.anbefal();
    p.append(anbefalBtn);
    this.anbefalEl = div('targetlist-list');
    p.append(this.anbefalEl);

    // ── Plasser ny kapabilitet ───────────────────────────────────────────
    // KART FØRST (UX_DESIGNSYSTEM.md §2: «kartet er helten» — og eierens egen
    // ordlyd: «plassere sensorer … direkte i kartet»). Primærhandlingen er
    // «Plasser i kartet»: velg type, klikk knappen, klikk ETT sted i kartet —
    // kapabiliteten opprettes DER, med typens standardprofil. Det manuelle
    // lat/lon-feltet under er et FALLBACK for et kjent koordinat (f.eks. fra
    // en rapport), ikke hovedveien.
    p.append(span('checklist-item-title', 'Plasser ny kapabilitet'));
    this.nyTypeSelect = document.createElement('select');
    this.nyTypeSelect.className = 'targetlist-btn';
    for (const t of ALLE_TYPER) {
      const o = document.createElement('option');
      o.value = t; o.textContent = KAPABILITET_TYPE_LABEL[t];
      this.nyTypeSelect.append(o);
    }
    p.append(this.nyTypeSelect);

    if (this.hooks.armPlassering) {
      const kartBtn = document.createElement('button');
      kartBtn.type = 'button'; kartBtn.className = 'targetlist-btn'; kartBtn.textContent = 'Plasser i kartet';
      kartBtn.onclick = () => {
        this.hooks.armPlassering!(this.nyTypeSelect.value as Kapabilitet['type']);
        this.melding('Klikk i kartet der kapabiliteten skal stå.', 'info');
      };
      p.append(kartBtn);
    }

    const manuellDetaljer = document.createElement('details');
    const manuellSammendrag = document.createElement('summary');
    manuellSammendrag.textContent = 'Eller angi et kjent koordinat';
    manuellSammendrag.className = 'checklist-note';
    manuellDetaljer.append(manuellSammendrag);
    const koordRad = div('kapabilitet-koordrad');
    this.nyLatInput = document.createElement('input');
    this.nyLatInput.type = 'number'; this.nyLatInput.step = 'any'; this.nyLatInput.placeholder = 'lat'; this.nyLatInput.className = 'report-input';
    this.nyLonInput = document.createElement('input');
    this.nyLonInput.type = 'number'; this.nyLonInput.step = 'any'; this.nyLonInput.placeholder = 'lon'; this.nyLonInput.className = 'report-input';
    koordRad.append(this.nyLatInput, this.nyLonInput);
    manuellDetaljer.append(koordRad);
    if (this.hooks.kartsenter) {
      const senterBtn = document.createElement('button');
      senterBtn.type = 'button'; senterBtn.className = 'targetlist-btn'; senterBtn.textContent = 'Bruk kartsenter';
      senterBtn.onclick = () => {
        const c = this.hooks.kartsenter!();
        this.nyLatInput.value = String(c.lat);
        this.nyLonInput.value = String(c.lon);
      };
      manuellDetaljer.append(senterBtn);
    }
    const plasserBtn = document.createElement('button');
    plasserBtn.type = 'button'; plasserBtn.className = 'targetlist-btn'; plasserBtn.textContent = 'Plasser på koordinat';
    plasserBtn.onclick = () => void this.plasserNy();
    manuellDetaljer.append(plasserBtn);
    p.append(manuellDetaljer);

    this.meldingEl = div('checklist-note');
    p.append(this.meldingEl);

    p.append(span('checklist-item-title', 'Bestillinger som venter'));
    this.bestillingEl = div('targetlist-list');
    p.append(this.bestillingEl);

    // SENS-42: godkjent ≠ utplassert. En godkjent bestilling er et løfte, ikke
    // en kapabilitet på kartet — operatøren må plassere DEN FYSISKE enheten
    // (via «Plasser i kartet» over) og deretter koble bestillingen til den her.
    p.append(span('checklist-item-title', 'Godkjent — klar til utplassering'));
    this.utplasserEl = div('targetlist-list kap-utplasser-liste');
    p.append(this.utplasserEl);

    p.append(span('checklist-item-title', 'Utplasserte kapabiliteter'));
    const oppdater = document.createElement('button');
    oppdater.type = 'button'; oppdater.className = 'targetlist-btn'; oppdater.textContent = 'Oppdater listen';
    oppdater.onclick = () => { void this.last(); void this.lastBestillinger(); };
    p.append(oppdater);
    this.listeEl = div('targetlist-list');
    p.append(this.listeEl);
    return p;
  }

  /** Kapabilitetene fra siste `last()` — leses av objektregisteret (main.ts)
   * for å vise dem i Objekter-lista, uten å duplisere nettverkskallet. */
  get gjeldende(): readonly Kapabilitet[] { return this.siste; }

  /** Hent kapabilitetslista på nytt. Feil VISES; svelges aldri til «tomt». */
  async last(): Promise<void> {
    try {
      const svar = lesKapabiliteterSvar(await this.hooks.hent('/kapabilitet'));
      this.siste = svar.kapabiliteter;
      this.hooks.tegnRinger(kapabiliteterFeatureCollection(this.siste));
      this.tegnListe();
      this.tegnUtplassering();
      if (svar.formfeil) this.melding(`⚠ ${svar.formfeil}`, 'varsel');
      this.hooks.onEndret?.();
    } catch (err) {
      this.siste = [];
      this.hooks.tegnRinger(featureCollection([]));
      this.listeEl.replaceChildren();
      this.tegnUtplassering();
      this.melding(`⚠ Kunne ikke lese kapabilitetsregisteret: ${feilTekst(err)}. Dette er en FEIL, ikke et tomt register.`, 'feil');
      this.hooks.onEndret?.();
    }
  }

  async lastBestillinger(): Promise<void> {
    try {
      const svar = lesBestillingerSvar(await this.hooks.hent('/kapabilitet/bestilling'));
      this.sisteBestillinger = svar.bestillinger;
      this.tegnBestillinger();
      this.tegnUtplassering();
    } catch (err) {
      this.sisteBestillinger = [];
      this.bestillingEl.replaceChildren();
      this.bestillingEl.append(feilSpan(`⚠ Kunne ikke lese bestillinger: ${feilTekst(err)}`));
      this.utplasserEl.replaceChildren();
    }
  }

  private async anbefal(): Promise<void> {
    const tekst = this.behovInput.value.trim();
    if (!tekst) { this.melding('Skriv en kort beskrivelse av behovet først.', 'varsel'); return; }
    this.anbefalEl.replaceChildren(span('checklist-note', 'Regner …'));
    try {
      const svar = (await this.hooks.send('/kapabilitet/anbefal', { tekst })) as { forslag?: KapabilitetForslag[] };
      const forslag = svar.forslag ?? [];
      this.anbefalEl.replaceChildren();
      if (!forslag.length) {
        this.anbefalEl.append(span('checklist-note', 'Ingen kapabiliteter i registeret å rangere mot ennå.'));
        return;
      }
      for (const f of forslag.slice(0, 10)) {
        const row = div('targetlist-row');
        const main = div('checklist-main');
        main.append(span('checklist-item-title',
          `${f.kapabilitet.navn?.trim() || KAPABILITET_TYPE_LABEL[f.kapabilitet.type]} — score ${f.score}`));
        for (const b of f.begrunnelse) main.append(span('checklist-note', b));
        row.append(main);
        this.anbefalEl.append(row);
      }
    } catch (err) {
      this.anbefalEl.replaceChildren(feilSpan(`⚠ Anbefaling feilet: ${feilTekst(err)}`));
    }
  }

  private async plasserNy(): Promise<void> {
    // ⚠ `Number('')` ER `0`, IKKE `NaN` — et tomt felt ville derfor sluppet
    // gjennom en `Number.isFinite`-sjekk ALENE og lagret «null island» (0,0)
    // uten at operatøren ba om det. Tomme felt sjekkes derfor eksplisitt FØR
    // konvertering.
    if (!this.nyLatInput.value.trim() || !this.nyLonInput.value.trim()) {
      this.melding('Sett en gyldig posisjon (lat/lon) først — eller «Bruk kartsenter».', 'varsel');
      return;
    }
    const lat = Number(this.nyLatInput.value);
    const lon = Number(this.nyLonInput.value);
    if (!Number.isFinite(lat) || !Number.isFinite(lon) || lat < -90 || lat > 90 || lon < -180 || lon > 180) {
      this.melding('Sett en gyldig posisjon (lat/lon) først — eller «Bruk kartsenter».', 'varsel');
      return;
    }
    await this.plasserPaaPunkt(this.nyTypeSelect.value as Kapabilitet['type'], lat, lon);
  }

  /**
   * Plasser en kapabilitet av `type` ved (lat, lon). Kalt av `plasserNy`
   * (manuelt koordinat) OG av main.ts (kartklikk, via `armPlassering`-hooken)
   * — SAMME kode uansett hvor punktet kom fra.
   */
  async plasserPaaPunkt(type: Kapabilitet['type'], lat: number, lon: number): Promise<void> {
    const utkast = nyKapabilitetMedProfil(type, { lat, lon }, this.hooks.naa());
    const id = `kapabilitet:${type}-${this.hooks.naa()}`;
    this.melding('Lagrer …', 'info');
    try {
      await this.hooks.send('/kapabilitet', { ...utkast, id });
      this.melding(`${KAPABILITET_TYPE_LABEL[type]} plassert.`, 'info');
      await this.last();
    } catch (err) {
      this.melding(`⚠ Kunne ikke plassere kapabiliteten: ${feilTekst(err)}`, 'feil');
    }
  }

  private tegnListe(): void {
    this.listeEl.replaceChildren();
    if (!this.siste.length) {
      this.listeEl.append(span('checklist-note', 'Ingen kapabiliteter plassert ennå.'));
      return;
    }
    for (const k of [...this.siste].sort((a, b) => b.opprettetMs - a.opprettetMs)) {
      this.listeEl.append(this.kapRad(k));
    }
  }

  private kapRad(k: Kapabilitet): HTMLElement {
    const row = div('targetlist-row');
    const dot = document.createElement('span');
    dot.className = 'checklist-dot';
    dot.style.background = 'var(--info)';
    const main = div('checklist-main');
    main.append(span('checklist-item-title', k.navn?.trim() || KAPABILITET_TYPE_LABEL[k.type]));
    const detaljer = [
      KAPABILITET_TYPE_LABEL[k.type],
      `rekkevidde ${(k.rekkeviddeM / 1000).toFixed(1)} km`,
      ...(k.identifiseringsrekkeviddeM !== undefined ? [`ID ${(k.identifiseringsrekkeviddeM / 1000).toFixed(1)} km`] : []),
      k.dagNatt === 'begge' ? 'dag/natt' : k.dagNatt,
    ];
    main.append(span('checklist-note', detaljer.join(' · ')));
    const feil = this.feilPerId.get(k.id);
    if (feil) main.append(feilSpan(`⚠ ${feil}`));

    const actions = div('targetlist-actions');
    if (this.hooks.flyTo) {
      const flyBtn = document.createElement('button');
      flyBtn.type = 'button'; flyBtn.className = 'targetlist-btn'; flyBtn.textContent = 'Vis';
      flyBtn.onclick = () => this.hooks.flyTo!(k.lat, k.lon);
      actions.append(flyBtn);
    }
    const slettBtn = document.createElement('button');
    slettBtn.type = 'button'; slettBtn.className = 'targetlist-btn';
    const sletter = this.ventendeSletting.has(k.id);
    slettBtn.disabled = sletter;
    slettBtn.textContent = sletter ? 'Sletter …' : 'Slett';
    slettBtn.onclick = () => void this.slett(k);
    actions.append(slettBtn);

    row.append(dot, main, actions);
    return row;
  }

  private async slett(k: Kapabilitet): Promise<void> {
    this.ventendeSletting.add(k.id);
    this.feilPerId.delete(k.id);
    this.tegnListe();
    try {
      await this.hooks.send('/kapabilitet/slett', { id: k.id });
      this.ventendeSletting.delete(k.id);
      await this.last();
    } catch (err) {
      this.ventendeSletting.delete(k.id);
      this.feilPerId.set(k.id, feilTekst(err));
      this.tegnListe();
    }
  }

  private tegnBestillinger(): void {
    this.bestillingEl.replaceChildren();
    const ventende = ventendeBestillinger(this.sisteBestillinger);
    if (!ventende.length) {
      this.bestillingEl.append(span('checklist-note', 'Ingen bestillinger venter.'));
      return;
    }
    for (const b of ventende) this.bestillingEl.append(this.bestillingRad(b));
  }

  private bestillingRad(b: Bestilling): HTMLElement {
    const row = div('targetlist-row');
    const main = div('checklist-main');
    main.append(span('checklist-item-title', b.behovTekst));
    main.append(span('checklist-note',
      [BESTILLING_STATUS_LABEL[b.status], `bestilt av ${short(b.bestiltAv)}`,
        b.onsketType ? KAPABILITET_TYPE_LABEL[b.onsketType] : null].filter(Boolean).join(' · ')));
    const feil = this.feilPerId.get(b.id);
    if (feil) main.append(feilSpan(`⚠ ${feil}`));

    const actions = div('targetlist-actions');
    const ventende = this.ventendeBehandling.has(b.id);
    const godkjennBtn = document.createElement('button');
    godkjennBtn.type = 'button'; godkjennBtn.className = 'targetlist-btn'; godkjennBtn.disabled = ventende;
    godkjennBtn.textContent = 'Godkjenn';
    godkjennBtn.onclick = () => void this.behandle(b, { handling: 'godkjenn' });
    const avvisBtn = document.createElement('button');
    avvisBtn.type = 'button'; avvisBtn.className = 'targetlist-btn'; avvisBtn.disabled = ventende;
    avvisBtn.textContent = 'Avvis';
    avvisBtn.onclick = () => {
      sporTekst({
        tittel: 'Avvis bestilling',
        tekst: 'Grunn for avvisning (påkrevd):',
        onSvar: (grunn) => {
          if (grunn?.trim()) void this.behandle(b, { handling: 'avvis', grunn: grunn.trim() });
        },
      });
    };
    actions.append(godkjennBtn, avvisBtn);
    row.append(main, actions);
    return row;
  }

  /** SENS-42: godkjente bestillinger, med en velger for HVILKEN allerede
   * plassert kapabilitet (fra `this.siste`) som svarer på løftet. */
  private tegnUtplassering(): void {
    this.utplasserEl.replaceChildren();
    const klare = klareTilUtplassering(this.sisteBestillinger);
    if (!klare.length) {
      this.utplasserEl.append(span('checklist-note', 'Ingen godkjente bestillinger venter på utplassering.'));
      return;
    }
    for (const b of klare) this.utplasserEl.append(this.utplasseringRad(b));
  }

  private utplasseringRad(b: Bestilling): HTMLElement {
    const row = div('targetlist-row');
    const main = div('checklist-main');
    main.append(span('checklist-item-title', b.behovTekst));
    main.append(span('checklist-note',
      [BESTILLING_STATUS_LABEL[b.status], `bestilt av ${short(b.bestiltAv)}`,
        b.onsketType ? KAPABILITET_TYPE_LABEL[b.onsketType] : null].filter(Boolean).join(' · ')));
    const feil = this.feilPerId.get(b.id);
    if (feil) main.append(feilSpan(`⚠ ${feil}`));

    const actions = div('targetlist-actions');
    const ventende = this.ventendeBehandling.has(b.id);
    if (!this.siste.length) {
      // Ærlig fravær (samme prinsipp som resten av panelet): ikke vis en
      // nedtrekksliste uten alternativer og et knapp som ikke kan lykkes — si
      // HVA operatøren må gjøre først.
      main.append(span('checklist-note',
        '⚠ Ingen kapabiliteter i registeret ennå. Plasser den i kartet ovenfor først.'));
      row.append(main, actions);
      return row;
    }
    const velg = document.createElement('select');
    velg.className = 'targetlist-btn';
    velg.disabled = ventende;
    const foreslatte = b.onsketType ? this.siste.filter((k) => k.type === b.onsketType) : [];
    const resten = this.siste.filter((k) => !foreslatte.includes(k));
    for (const k of [...foreslatte, ...resten]) {
      const o = document.createElement('option');
      o.value = k.id;
      o.textContent = `${k.navn?.trim() || KAPABILITET_TYPE_LABEL[k.type]} (${short(k.id)})`;
      velg.append(o);
    }
    const utplasserBtn = document.createElement('button');
    utplasserBtn.type = 'button'; utplasserBtn.className = 'targetlist-btn'; utplasserBtn.disabled = ventende;
    utplasserBtn.textContent = 'Utplasser';
    utplasserBtn.onclick = () => void this.behandle(b, { handling: 'utplasser', kapabilitetId: velg.value });
    actions.append(velg, utplasserBtn);
    row.append(main, actions);
    return row;
  }

  private async behandle(b: Bestilling, kropp: Record<string, unknown>): Promise<void> {
    this.ventendeBehandling.add(b.id);
    this.feilPerId.delete(b.id);
    this.tegnBestillinger();
    this.tegnUtplassering();
    try {
      await this.hooks.send('/kapabilitet/bestilling/behandle', { id: b.id, ...kropp });
      this.ventendeBehandling.delete(b.id);
      await this.lastBestillinger();
    } catch (err) {
      this.ventendeBehandling.delete(b.id);
      this.feilPerId.set(b.id, feilTekst(err));
      this.tegnBestillinger();
      this.tegnUtplassering();
    }
  }

  private melding(tekst: string, art: 'info' | 'varsel' | 'feil'): void {
    this.meldingEl.textContent = tekst;
    this.meldingEl.style.color = art === 'feil' ? 'var(--danger)' : art === 'varsel' ? 'var(--gold)' : '';
  }
}

// ── Nettverk (standardimplementasjonen main.ts sender inn) ───────────────────
// Samme mønster som personregisterHent/-Send i ui/personregister.ts.

async function feilAvSvar(r: Response): Promise<string> {
  try {
    const j = (await r.json()) as { error?: unknown };
    if (typeof j?.error === 'string') return `${r.status} ${j.error}`;
  } catch { /* ikke JSON — statuskoden er alt vi har */ }
  return `${r.status} ${r.statusText || 'feil'}`;
}

export async function kapabilitetHent(sti: string): Promise<unknown> {
  const r = await fetch(sti, { credentials: 'include' });
  if (!r.ok) throw new Error(await feilAvSvar(r));
  return r.json();
}

export async function kapabilitetSend(sti: string, kropp: unknown): Promise<unknown> {
  const r = await fetch(sti, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(kropp),
  });
  if (!r.ok) throw new Error(await feilAvSvar(r));
  return r.json();
}

function feilTekst(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

function div(cls: string): HTMLElement { const e = document.createElement('div'); e.className = cls; return e; }
function span(cls: string, text: string): HTMLElement { const e = document.createElement('span'); e.className = cls; e.textContent = text; return e; }
function feilSpan(text: string): HTMLElement {
  const e = span('checklist-note', text);
  e.style.color = 'var(--danger)';
  return e;
}
function short(id: string): string { return id.replace(/^@/, '').replace(/:.*$/, ''); }
